import { Box, Text } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';
import {
  formatTablePrimitiveAccounting,
  formatTablePrimitiveAddress,
  formatTablePrimitiveDate,
  formatTablePrimitiveDateRange,
  formatTablePrimitiveNumber,
  TABLE_PRIMITIVE_VALUE_PLACEHOLDER,
  type TablePrimitiveAddressValue,
  type TablePrimitiveDateInput,
  type TablePrimitiveDateRangeValue,
} from './TablePrimitiveValue.utils';

type BaseProps = {
  placeholder?: string;
};

type DateProps = BaseProps & {
  type: 'date';
  value: TablePrimitiveDateInput | null | undefined;
};

type DateRangeProps = BaseProps & {
  type: 'dateRange';
  value: TablePrimitiveDateRangeValue | null | undefined;
};

type StringProps = BaseProps & {
  type: 'string' | 'longString';
  value: string | null | undefined;
};

type AccountingProps = BaseProps & {
  currency?: string;
  type: 'accounting';
  value: number | string | null | undefined;
};

type NumberProps = BaseProps & {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  prefix?: string;
  suffix?: string;
  type: 'number';
  value: number | string | null | undefined;
};

type AddressProps = BaseProps & {
  type: 'address';
  value: TablePrimitiveAddressValue | string | null | undefined;
};

export type TablePrimitiveValueProps =
  | AccountingProps
  | AddressProps
  | DateProps
  | DateRangeProps
  | NumberProps
  | StringProps;

const PrimitiveText = ({
  children,
  color,
  fontFeatureSettings,
  fontSize = 13,
  letterSpacing = '-0.09px',
  lineClamp,
  lineHeight = 1.4,
  textAlign,
  whiteSpace,
}: {
  children: ReactNode;
  color: string;
  fontFeatureSettings?: CSSProperties['fontFeatureSettings'];
  fontSize?: number;
  letterSpacing?: string;
  lineClamp?: number;
  lineHeight?: number;
  textAlign?: 'left' | 'right';
  whiteSpace?: CSSProperties['whiteSpace'];
}) => (
  <Text
    c={color}
    display="block"
    fw={400}
    fz={fontSize}
    lh={lineHeight}
    lineClamp={lineClamp}
    style={{
      fontFeatureSettings,
      letterSpacing,
      textAlign,
      whiteSpace,
      width: '100%',
    }}
  >
    {children}
  </Text>
);

export const TablePrimitiveValue = (props: TablePrimitiveValueProps) => {
  const placeholder = props.placeholder || TABLE_PRIMITIVE_VALUE_PLACEHOLDER;
  const textColor = '#262626';
  const mutedTextColor = '#A3A3A3';

  if (props.type === 'date') {
    const formatted = formatTablePrimitiveDate(props.value, props.placeholder);

    return (
      <PrimitiveText
        color={formatted === placeholder ? mutedTextColor : textColor}
      >
        {formatted}
      </PrimitiveText>
    );
  }

  if (props.type === 'dateRange') {
    const formatted = formatTablePrimitiveDateRange(
      props.value,
      props.placeholder
    );

    return (
      <PrimitiveText
        color={formatted === placeholder ? mutedTextColor : textColor}
      >
        {formatted}
      </PrimitiveText>
    );
  }

  if (props.type === 'string') {
    const value = props.value || placeholder;

    return (
      <PrimitiveText color={props.value ? textColor : mutedTextColor}>
        {value}
      </PrimitiveText>
    );
  }

  if (props.type === 'longString') {
    const value = props.value || placeholder;

    return (
      <PrimitiveText
        color={props.value ? textColor : mutedTextColor}
        fontSize={14}
        lineClamp={2}
        lineHeight={1.2}
      >
        {value}
      </PrimitiveText>
    );
  }

  if (props.type === 'accounting') {
    const formatted = formatTablePrimitiveAccounting({
      currency: props.currency,
      placeholder: props.placeholder,
      value: props.value,
    });

    return (
      <PrimitiveText
        color={formatted === placeholder ? mutedTextColor : textColor}
        fontFeatureSettings="'ss01' 1, 'zero' 1, 'calt' 0, 'lnum' 1, 'tnum' 1"
        fontSize={14}
        textAlign="right"
        whiteSpace="nowrap"
      >
        {formatted}
      </PrimitiveText>
    );
  }

  if (props.type === 'number') {
    const formatted = formatTablePrimitiveNumber({
      maximumFractionDigits: props.maximumFractionDigits,
      minimumFractionDigits: props.minimumFractionDigits,
      placeholder: props.placeholder,
      prefix: props.prefix,
      suffix: props.suffix,
      value: props.value,
    });

    return (
      <PrimitiveText
        color={formatted === placeholder ? mutedTextColor : textColor}
        fontSize={12}
        letterSpacing="0.01px"
        textAlign="right"
      >
        {formatted}
      </PrimitiveText>
    );
  }

  const formatted = formatTablePrimitiveAddress(props.value, props.placeholder);

  return (
    <Box maw="100%">
      <PrimitiveText
        color={formatted === placeholder ? mutedTextColor : textColor}
        lineClamp={1}
      >
        {formatted}
      </PrimitiveText>
    </Box>
  );
};
